import React, { useEffect, useRef, useState } from 'react';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonialsData } from '../../data/mock';
import testimonialImage from '@/assets/images/happy-mother-child.jpg';
import happyVideo from '@/assets/images/happy.mp4';

const TestimonialsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % testimonialsData.length);
  };

  const prevTestimonial = () => {
    setActiveIndex((prev) => (prev - 1 + testimonialsData.length) % testimonialsData.length);
  };

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className="py-32 bg-white relative overflow-hidden"
    >
      {/* Background Image */}
      {/* <div className="absolute inset-0 opacity-5">
        <img 
          src="https://images.unsplash.com/photo-1511895426328-dc8714191300?w=1200&q=80" 
          alt=""
          className="w-full h-full object-cover"
        />
      </div> */}

      <div className="max-w-6xl mx-auto px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <span className={`inline-block text-sm text-[#202020]/40 uppercase tracking-widest mb-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            Real Stories
          </span>
          <h2 className={`text-3xl md:text-4xl lg:text-5xl font-normal text-[#202020] tracking-tight transition-all duration-1000 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            What parents are experiencing
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Featured Testimonial */}
          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="relative">
              {/* Quote Icon */}
              <div className="absolute -top-8 left-0">
                <Quote className="w-16 h-16 text-[#202020]/5" />
              </div>

              {/* Quote */}
              <blockquote className="text-2xl md:text-3xl text-[#202020] leading-relaxed mb-10 font-light">
                &ldquo;{testimonialsData[activeIndex].quote}&rdquo;
              </blockquote>

              {/* Author */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <img
                    src={testimonialsData[activeIndex].image}
                    alt={testimonialsData[activeIndex].author}
                    className="w-14 h-14 rounded-lg object-cover"
                  />
                  <div>
                    <p className="font-medium text-[#202020]">{testimonialsData[activeIndex].author}</p>
                    <p className="text-[#202020]/50 text-sm">{testimonialsData[activeIndex].childAge}</p>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={prevTestimonial}
                    className="w-12 h-12 rounded-lg border border-[#202020]/20 flex items-center justify-center hover:bg-[#202020] hover:text-[#efede6] hover:border-[#202020] transition-all"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextTestimonial}
                    className="w-12 h-12 rounded-lg border border-[#202020]/20 flex items-center justify-center hover:bg-[#202020] hover:text-[#efede6] hover:border-[#202020] transition-all"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Dots */}
              <div className="flex items-center gap-2 mt-8">
                {testimonialsData.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveIndex(index)}
                    className={`h-1.5 rounded-lg transition-all ${index === activeIndex ? 'bg-[#202020] w-8' : 'bg-[#202020]/20 w-1.5 hover:bg-[#202020]/40'}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right - Emotional Image */}
          <div className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <video
                  src={happyVideo}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-84 md:h-96 object-cover grayscale brightness-90"
                />
                {/* <img 
                  src={testimonialImage} 
                  alt="Happy parent and child moment"
                  className="w-full h-[500px] object-cover grayscale brightness-90"
                /> */}
              </div>
              {/* <div className="absolute -bottom-4 -left-4 bg-[#efede6] rounded-2xl p-5 shadow-lg">
                <p className="text-[#202020] font-medium text-2xl">500+</p>
                <p className="text-[#202020]/50 text-sm">Peaceful families</p>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
